<script setup>
import { computed, onUnmounted, ref } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Policy from 'dashboard/components/policy.vue';
import DateTimePicker from 'dashboard/components/ui/DateTimePicker.vue';
import MeetingInvitesAPI from 'dashboard/api/meetingInvites';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const [showActionsDropdown, toggleDropdown] = useToggle(false);
const showMeetingModal = ref(false);
const meetingMode = ref('instant');
const presentationTitle = ref('');
const scheduledAt = ref(null);
const isDownloadingTranscript = ref(false);
const meetingInviteLink = ref('');
const agentMeetingInviteLink = ref('');
const isCreatingMeeting = ref(false);
const isSendingMeetingLink = ref(false);
const isOpeningMeetingLink = ref(false);

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);
const currentUser = computed(() => store.getters.getCurrentUser);
const isChatCallEnabled = computed(() =>
  store.getters['accounts/isFeatureEnabledonAccount'](
    accountId.value,
    FEATURE_FLAGS.CHAT_CALLS
  )
);

const meetingInviteLabel = t('CONVERSATION.MEETING_INVITE.BUTTON');
const canSubmitSchedule = computed(
  () => meetingMode.value === 'instant' || !!scheduledAt.value
);

const actionMenuItems = computed(() => []);

const toggleMuteFromToolbar = () => {
  if (!currentChat.value?.id) return;
  if (currentChat.value.muted) {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  }
};

const handleActionClick = ({ action }) => {
  toggleDropdown(false);

  if (action === 'mute') {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } else if (action === 'unmute') {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  }
};

const mute = () => {
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

const downloadConversationTranscript = async () => {
  if (!currentChat.value?.id || isDownloadingTranscript.value) return;
  isDownloadingTranscript.value = true;
  try {
    await store.dispatch('downloadTranscript', {
      conversationId: currentChat.value.id,
    });
    useAlert(t('EMAIL_TRANSCRIPT.DOWNLOAD_SUCCESS'));
  } catch (error) {
    useAlert(t('EMAIL_TRANSCRIPT.DOWNLOAD_ERROR'));
  } finally {
    isDownloadingTranscript.value = false;
  }
};

if (isAdmin.value) {
  emitter.on(CMD_MUTE_CONVERSATION, mute);
  emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
}
emitter.on(CMD_SEND_TRANSCRIPT, downloadConversationTranscript);

const copyMeetingInvite = async () => {
  if (!meetingInviteLink.value) return;
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(meetingInviteLink.value);
      useAlert(t('CONVERSATION.MEETING_INVITE.LINK_COPIED'));
    } else {
      useAlert(meetingInviteLink.value);
    }
  } catch (error) {
    useAlert(t('CONVERSATION.MEETING_INVITE.COPY_ERROR'));
  }
};

const copyAgentMeetingInvite = async () => {
  if (!agentMeetingInviteLink.value) return;
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(agentMeetingInviteLink.value);
      useAlert(t('CONVERSATION.MEETING_INVITE.AGENT_LINK_COPIED'));
    } else {
      useAlert(agentMeetingInviteLink.value);
    }
  } catch (error) {
    useAlert(t('CONVERSATION.MEETING_INVITE.COPY_AGENT_ERROR'));
  }
};

const sendMeetingInviteLink = async () => {
  if (!meetingInviteLink.value || !currentChat.value?.id) return;
  isSendingMeetingLink.value = true;
  try {
    await store.dispatch('createPendingMessageAndSend', {
      message: meetingInviteLink.value,
      conversationId: currentChat.value.id,
    });
    useAlert(t('CONVERSATION.MEETING_INVITE.LINK_SENT'));
    showMeetingModal.value = false;
  } catch (error) {
    useAlert(t('CONVERSATION.MEETING_INVITE.SEND_ERROR'));
  } finally {
    isSendingMeetingLink.value = false;
  }
};

const goToMeetingInvite = () => {
  const targetUrl = agentMeetingInviteLink.value || meetingInviteLink.value;
  if (!targetUrl || isOpeningMeetingLink.value) return;

  isOpeningMeetingLink.value = true;
  window.open(targetUrl, '_blank', 'noopener,noreferrer');

  window.setTimeout(() => {
    isOpeningMeetingLink.value = false;
  }, 300);
};

const resetMeetingForm = () => {
  meetingMode.value = 'instant';
  presentationTitle.value = '';
  scheduledAt.value = null;
  meetingInviteLink.value = '';
  agentMeetingInviteLink.value = '';
};

const closeMeetingModal = () => {
  showMeetingModal.value = false;
  resetMeetingForm();
};

const openMeetingModal = () => {
  if (!currentChat.value?.id || !isChatCallEnabled.value) return;
  resetMeetingForm();
  showMeetingModal.value = true;
};

const onScheduleChange = value => {
  scheduledAt.value = value;
};

const createMeetingInvite = async () => {
  if (!currentChat.value?.id || !isChatCallEnabled.value) return;
  if (meetingMode.value === 'schedule' && !scheduledAt.value) {
    useAlert(t('CONVERSATION.MEETING_INVITE.SCHEDULE_REQUIRED'));
    return;
  }

  const isScheduling = meetingMode.value === 'schedule';
  isCreatingMeeting.value = true;
  try {
    const payload = isScheduling
      ? {
          scheduled_at: new Date(scheduledAt.value).toISOString(),
          title: presentationTitle.value.trim(),
        }
      : {};

    const response = await MeetingInvitesAPI.create(
      accountId.value,
      currentChat.value.id,
      currentUser.value?.access_token,
      payload
    );

    const invite = response.data?.meeting_invite || {};

    if (isScheduling) {
      if (invite.status !== 'scheduled') {
        useAlert(t('CONVERSATION.MEETING_INVITE.CREATE_ERROR'));
        return;
      }

      useAlert(
        t('CONVERSATION.MEETING_INVITE.SCHEDULE_SUCCESS_DETAIL', {
          time: invite.scheduled_at
            ? new Date(invite.scheduled_at).toLocaleString()
            : '',
        })
      );
      closeMeetingModal();
      return;
    }

    meetingInviteLink.value = invite.join_url || '';
    agentMeetingInviteLink.value =
      invite.agent_join_url || meetingInviteLink.value;

    if (!meetingInviteLink.value) {
      useAlert(t('CONVERSATION.MEETING_INVITE.CREATE_ERROR'));
      return;
    }

    useAlert(t('CONVERSATION.MEETING_INVITE.CREATE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CONVERSATION.MEETING_INVITE.CREATE_ERROR')
    );
  } finally {
    isCreatingMeeting.value = false;
  }
};

onUnmounted(() => {
  if (isAdmin.value) {
    emitter.off(CMD_MUTE_CONVERSATION, mute);
    emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  }
  emitter.off(CMD_SEND_TRANSCRIPT, downloadConversationTranscript);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <Policy :permissions="['administrator']">
      <ButtonV4
        v-tooltip="
          currentChat?.muted
            ? t('CONTACT_PANEL.UNMUTE_CONTACT')
            : t('CONTACT_PANEL.MUTE_CONTACT')
        "
        :aria-label="
          currentChat?.muted
            ? t('CONTACT_PANEL.UNMUTE_CONTACT')
            : t('CONTACT_PANEL.MUTE_CONTACT')
        "
        :title="
          currentChat?.muted
            ? t('CONTACT_PANEL.UNMUTE_CONTACT')
            : t('CONTACT_PANEL.MUTE_CONTACT')
        "
        size="sm"
        variant="solid"
        color="slate"
        :icon="currentChat?.muted ? 'i-lucide-volume-1' : 'i-lucide-ban'"
        :disabled="!currentChat?.id"
        class="flex-shrink-0 rounded-lg shadow outline outline-1 outline-n-container"
        @click="toggleMuteFromToolbar"
      />
    </Policy>
    <ButtonV4
      v-if="isChatCallEnabled"
      v-tooltip="meetingInviteLabel"
      :aria-label="meetingInviteLabel"
      :title="meetingInviteLabel"
      size="sm"
      variant="solid"
      color="slate"
      icon="i-lucide-video"
      :disabled="!currentChat?.id"
      class="flex-shrink-0 rounded-lg shadow outline outline-1 outline-n-container"
      @click="openMeetingModal"
    />
    <ButtonV4
      v-tooltip="t('CONTACT_PANEL.SEND_TRANSCRIPT')"
      :aria-label="t('CONTACT_PANEL.SEND_TRANSCRIPT')"
      :title="t('CONTACT_PANEL.SEND_TRANSCRIPT')"
      size="sm"
      variant="solid"
      color="slate"
      icon="i-lucide-download"
      :is-loading="isDownloadingTranscript"
      :disabled="!currentChat?.id || isDownloadingTranscript"
      class="flex-shrink-0 rounded-lg shadow outline outline-1 outline-n-container"
      @click="downloadConversationTranscript"
    />
    <ResolveAction
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <div
      v-if="actionMenuItems.length > 0"
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-items="actionMenuItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
    </div>
    <woot-modal
      v-model:show="showMeetingModal"
      :on-close="closeMeetingModal"
      size="medium"
    >
      <woot-modal-header
        :header-title="t('CONVERSATION.MEETING_INVITE.MODAL_TITLE')"
      />
      <div class="p-4 space-y-4">
        <template v-if="!meetingInviteLink">
          <div class="grid grid-cols-2 gap-2">
            <button
              type="button"
              class="rounded-lg border px-3 py-2 text-sm font-medium transition-colors"
              :class="
                meetingMode === 'instant'
                  ? 'border-n-brand bg-n-brand/10 text-n-brand'
                  : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
              "
              @click="meetingMode = 'instant'"
            >
              {{ t('CONVERSATION.MEETING_INVITE.MODE_INSTANT') }}
            </button>
            <button
              type="button"
              class="rounded-lg border px-3 py-2 text-sm font-medium transition-colors"
              :class="
                meetingMode === 'schedule'
                  ? 'border-n-brand bg-n-brand/10 text-n-brand'
                  : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
              "
              @click="meetingMode = 'schedule'"
            >
              {{ t('CONVERSATION.MEETING_INVITE.MODE_SCHEDULE') }}
            </button>
          </div>

          <p class="text-sm text-n-slate-11">
            {{
              meetingMode === 'schedule'
                ? t('CONVERSATION.MEETING_INVITE.SCHEDULE_HELP')
                : t('CONVERSATION.MEETING_INVITE.INSTANT_HELP')
            }}
          </p>

          <div v-if="meetingMode === 'schedule'" class="space-y-3">
            <label class="block space-y-1">
              <span class="text-xs font-medium text-n-slate-11">
                {{ t('CONVERSATION.MEETING_INVITE.TITLE_LABEL') }}
              </span>
              <input
                v-model="presentationTitle"
                class="w-full input"
                type="text"
                :placeholder="t('CONVERSATION.MEETING_INVITE.TITLE_PLACEHOLDER')"
              />
            </label>
            <label class="block space-y-1">
              <span class="text-xs font-medium text-n-slate-11">
                {{ t('CONVERSATION.MEETING_INVITE.SCHEDULE_AT') }}
              </span>
              <DateTimePicker
                confirm-text="OK"
                :placeholder="t('CONVERSATION.MEETING_INVITE.SCHEDULE_PLACEHOLDER')"
                :value="scheduledAt"
                @change="onScheduleChange"
              />
            </label>
          </div>

          <div class="flex items-center justify-end gap-2">
            <woot-button
              type="button"
              variant="clear"
              color-scheme="secondary"
              @click="closeMeetingModal"
            >
              {{ t('CONVERSATION.MEETING_INVITE.CANCEL') }}
            </woot-button>
            <woot-button
              type="button"
              color-scheme="primary"
              variant="solid"
              :loading="isCreatingMeeting"
              :disabled="!canSubmitSchedule || isCreatingMeeting"
              @click="createMeetingInvite"
            >
              {{
                meetingMode === 'schedule'
                  ? t('CONVERSATION.MEETING_INVITE.CONFIRM_SCHEDULE')
                  : t('CONVERSATION.MEETING_INVITE.CONFIRM_INSTANT')
              }}
            </woot-button>
          </div>
        </template>

        <template v-else>
          <div class="space-y-1">
            <div class="text-xs font-medium text-n-slate-11">
              {{ t('CONVERSATION.MEETING_INVITE.CUSTOMER_LINK') }}
            </div>
            <div class="flex items-center gap-2">
              <input class="w-full input" readonly :value="meetingInviteLink" />
              <woot-button
                type="button"
                variant="clear"
                size="small"
                icon="copy"
                :disabled="!meetingInviteLink"
                @click="copyMeetingInvite"
              />
            </div>
          </div>
          <div class="space-y-1">
            <div class="text-xs font-medium text-n-slate-11">
              {{ t('CONVERSATION.MEETING_INVITE.AGENT_LINK') }}
            </div>
            <div class="flex items-center gap-2">
              <input
                class="w-full input"
                readonly
                :value="agentMeetingInviteLink"
              />
              <woot-button
                type="button"
                variant="clear"
                size="small"
                icon="copy"
                :disabled="!agentMeetingInviteLink"
                @click="copyAgentMeetingInvite"
              />
            </div>
            <p class="text-xs text-n-slate-11">
              {{ t('CONVERSATION.MEETING_INVITE.AGENT_LINK_HELP') }}
            </p>
          </div>
          <div class="flex items-center gap-2 justify-end">
            <woot-button
              type="button"
              variant="clear"
              color-scheme="secondary"
              :disabled="!agentMeetingInviteLink"
              @click="copyAgentMeetingInvite"
            >
              {{ t('CONVERSATION.MEETING_INVITE.COPY_AGENT') }}
            </woot-button>
            <woot-button
              type="button"
              variant="clear"
              color-scheme="secondary"
              :loading="isSendingMeetingLink"
              :disabled="!meetingInviteLink"
              @click="sendMeetingInviteLink"
            >
              {{ t('CONVERSATION.MEETING_INVITE.SEND') }}
            </woot-button>
            <woot-button
              type="button"
              color-scheme="primary"
              variant="solid"
              :disabled="!meetingInviteLink || isOpeningMeetingLink"
              @click="goToMeetingInvite"
            >
              {{ t('CONVERSATION.MEETING_INVITE.JOIN') }}
            </woot-button>
          </div>
        </template>
      </div>
    </woot-modal>
  </div>
</template>
